import { useEffect, useRef } from 'react'

import { EDITOR_DRAG_DETECTION_THRESHOLD } from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { CircleMeshProps } from '@editor/interfaces/canvas/circleMesh'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { RootState, useAppDispatch } from '@editor/store/app'
import {
  setCurrentAnchorPosition,
  setCursor,
  setEditingElementId,
  setHoveredElementId,
  setSelectedElementIds,
  toggleSelectedElementIds,
} from '@editor/store/tool'
import { ThreeEvent } from '@react-three/fiber'
import { useSelector } from 'react-redux'
import { Matrix4Tuple, Vector2, Vector3 } from 'three'

const useEventHandlers = ({ id, transformation, radius, isWorking, isEditing, onComplete }: CircleMeshProps) => {
  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)

  const isSelectorTool = selectedTool === BlueprintEditorTools.Selector

  const movingStartLocalPointRef = useRef<Vector2 | null>(null)
  const tempV2Ref = useRef(new Vector2())

  const isDraggingRef = useRef(false)
  const lastPositionRef = useRef<Vector3 | null>(null)
  const lastAnchorPositionRef = useRef<Vector3 | null>(null)

  const handleMouseEnter = (ev: ThreeEvent<MouseEvent>) => {
    if (isEditing || isWorking) return

    ev.stopPropagation()
    dispatch(setCurrentAnchorPosition(undefined))
    dispatch(setHoveredElementId(id))
    dispatch(setCursor(CursorState.MOVE))
  }

  const handleMouseLeave = (ev: ThreeEvent<MouseEvent>) => {
    ev.stopPropagation()
    dispatch(setHoveredElementId(''))
    dispatch(setCursor(CursorState.NONE))
  }

  const handleMouseDown = (ev: ThreeEvent<MouseEvent>) => {
    ev.stopPropagation()
    movingStartLocalPointRef.current = new Vector2(ev.clientX, ev.clientY)
  }

  const handleClick = (ev: ThreeEvent<MouseEvent>) => {
    if (isDraggingRef.current || !isSelectorTool) return

    ev.stopPropagation()
    dispatch(setSelectedElementIds([id]))
  }

  useEffect(() => {
    const handleMouseUp = (ev: MouseEvent) => {
      movingStartLocalPointRef.current = null

      if (!isEditing || !isDraggingRef.current) return

      ev.stopPropagation()

      if (lastPositionRef.current) {
        const updatedTransformation: Matrix4Tuple = [...transformation]
        const { x, y, z } = lastPositionRef.current
        updatedTransformation[12] = x
        updatedTransformation[13] = y
        updatedTransformation[14] = z

        onComplete?.(updatedTransformation, radius)
      }

      dispatch(setEditingElementId(undefined))
      dispatch(setCurrentAnchorPosition(undefined))
      dispatch(toggleSelectedElementIds({ ids: [id], selected: true }))
      isDraggingRef.current = false
      lastPositionRef.current = null
      lastAnchorPositionRef.current = null
    }

    const handleMouseMove = (ev: MouseEvent) => {
      if (movingStartLocalPointRef.current) {
        const distance = movingStartLocalPointRef.current.distanceTo(tempV2Ref.current.set(ev.clientX, ev.clientY))
        if (distance > EDITOR_DRAG_DETECTION_THRESHOLD && !isDraggingRef.current) {
          isDraggingRef.current = true
          dispatch(setEditingElementId(id))
          dispatch(setHoveredElementId(''))
        }
      }
    }

    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [isEditing, id, transformation, lastPositionRef, radius, onComplete, dispatch])

  return {
    isDraggingRef,
    lastPositionRef,
    lastAnchorPositionRef,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleClick,
  }
}

export default useEventHandlers
